// assets/signet-engraver.js
import { Component } from '@theme/component';

/**
 * @typedef {object} SignetEngraverRefs
 * @property {HTMLInputElement[]} modeRadios
 * @property {HTMLElement} wappenArea
 * @property {HTMLElement} initialsArea
 * @property {HTMLElement} uploadArea
 * @property {HTMLInputElement} nameInput
 * @property {HTMLButtonElement} generateButton
 * @property {HTMLInputElement} initial1
 * @property {HTMLInputElement} initial2
 * @property {HTMLButtonElement} initialsButton
 * @property {HTMLButtonElement} uploadButton
 * @property {HTMLInputElement} fileInput
 * @property {HTMLElement} status
 * @property {HTMLElement} previewWrap
 * @property {HTMLImageElement} preview
 * @property {HTMLElement} confirmWrap
 * @property {HTMLInputElement} confirmCheckbox
 * @property {HTMLInputElement} idInput
 * @property {HTMLInputElement} previewUrlInput
 * @property {HTMLInputElement} vectorUrlInput
 * @property {HTMLInputElement} sourceInput
 * @property {HTMLInputElement} initialsInput
 * @property {HTMLInputElement} confirmedInput
 * @property {HTMLButtonElement} removeButton
 * @property {HTMLAnchorElement} [fallbackLink]
 */

const UPLOAD_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const MAX_EDGE = 1024;

/** Die drei Eingabe-Arten der Segmented-Control. */
const MODES = ['wappen', 'initialen', 'upload'];

/** Interne Herkunft → sichtbarer Wert der Property _wappen_source. */
const SOURCE_LABELS = { generated: 'generiert', uploaded: 'hochgeladen', initials: 'initialen' };

/**
 * Ruhig formulierte Meldungen für die Fehlercodes, die der Upload-Endpoint
 * zurückgeben kann. Alles andere fällt auf die generische Upload-Meldung.
 */
const UPLOAD_ERROR_MESSAGES = {
  image_no_content: 'Das Bild wirkt leer oder einfarbig. Bitte ein kontrastreiches Motiv verwenden.',
  image_low_contrast: 'Das Bild hat zu wenig Kontrast für eine Gravur. Bitte ein klareres Motiv verwenden.',
  image_too_small: 'Das Bild ist zu klein für eine Gravur. Bitte verwende ein Motiv mit mindestens 512 Pixel Kantenlänge.',
};

/**
 * Ruhig formulierte Meldungen für die Fehlercodes, die der Initialen-Endpoint
 * zurückgeben kann. Alles andere fällt auf die generische Monogramm-Meldung.
 */
const INITIALS_ERROR_MESSAGES = {
  invalid_initials: 'Für ein Monogramm werden zwei Buchstaben benötigt.',
  rate_limited: 'Zu viele Anfragen kurz hintereinander. Bitte warte einen Moment und versuche es erneut.',
};

/**
 * Formatiert zwei Initialen zur Property-Schreibweise „M.W." — zwei
 * Großbuchstaben mit Punkt dazwischen und Punkt am Ende.
 * @param {string} raw
 * @returns {string}
 */
function formatInitials(raw) {
  const chars = (raw || '').toUpperCase().slice(0, 2);
  if (chars.length < 2) return '';
  return `${chars[0]}.${chars[1]}.`;
}

/**
 * GUSTHÓ Siegelring-Gravur-Konfigurator.
 *
 * Drei Wege zum Motiv, über eine Segmented-Control umschaltbar:
 *   Wappen    — Familienname → App-Proxy-Endpoint → KI-Wappen.
 *   Initialen — zwei Buchstaben → App-Proxy-Endpoint → Monogramm.
 *   Eigenes Bild — Datei → Canvas-Downscale → App-Proxy-Endpoint → S/W +
 *                  Vektorisierung.
 * Alle drei zeigen eine Vorschau und schreiben die Asset-URLs in versteckte
 * Line-Item-Properties. Der Ring ist auch ohne Motiv bestellbar. Wer keinen
 * Vorschlag mag, kann uns über den mailto-Fallback schreiben.
 *
 * Generierte Wappen sind an den Namen, Monogramme an die Initialen gebunden
 * (Änderung verwirft sie); hochgeladene Motive bleiben bestehen. Hochgeladene
 * Motive müssen vor dem Kauf bestätigt werden (siehe #onFormSubmit).
 *
 * @extends Component<SignetEngraverRefs>
 */
class SignetEngraver extends Component {
  /** Aktive Eingabe-Art. @type {'wappen' | 'initialen' | 'upload'} */
  #mode = 'wappen';
  /** Bindungsschlüssel des aktuellen Motivs: Name (Wappen) bzw. rohe Initialen. */
  #approvedName = null;
  /** Herkunft des aktuellen Motivs. @type {'generated' | 'uploaded' | 'initials' | null} */
  #source = null;

  connectedCallback() {
    super.connectedCallback();

    // Generierten/hochgeladenen Zustand nach Re-Render / Rück-Navigation wiederherstellen.
    this.#hydrateFromStorage();

    // Der Ring ist auch ohne Motiv bestellbar — kein generelles Submit-Gating.
    // mailto-Fallback initial mit dem aktuellen Namen aufbauen.
    this.#updateFallbackHref();

    // Ein hochgeladenes, noch nicht bestätigtes Motiv blockiert den Kauf.
    // Der Theme-Buy-Button lässt sich nicht block-lokal dauerhaft deaktivieren
    // (product-form-component reaktiviert ihn bei jedem Variantenwechsel, also
    // auch beim Metallwechsel). Deshalb fangen wir das Submit des Produkt-
    // formulars ab. Der delegierte Theme-Handler hängt auf document (Capture);
    // ein Capture-Listener auf window greift eine Stufe früher.
    window.addEventListener('submit', this.#onFormSubmit, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('submit', this.#onFormSubmit, { capture: true });
  }

  updatedCallback() {
    super.updatedCallback();
    // Nach einem Section-Re-Render (Section Rendering API) sind die frisch
    // gerenderten Inputs leer und die mailto-href statisch. Zustand aus dem
    // sessionStorage und den Fallback-Link neu aufbauen, sonst gingen ein
    // bereits erzeugtes Motiv bzw. der vorausgefüllte Betreff/Text verloren.
    this.#hydrateFromStorage();
    this.#updateFallbackHref();
  }

  /** Wechselt die Eingabe-Art. Ein vorhandenes Motiv bleibt erhalten. @param {Event} [event] */
  handleModeChange(event) {
    const value = /** @type {HTMLInputElement | undefined} */ (event?.target)?.value;
    if (value && MODES.includes(value)) this.#setMode(value);
  }

  handleNameInput() {
    // Ein geänderter Name macht ein GENERIERTES Wappen ungültig (es ist an den
    // Namen gebunden). Hochgeladene Motive und Monogramme bleiben unberührt.
    const current = this.refs.nameInput.value.trim();
    if (this.#source === 'generated' && this.#approvedName !== null && current !== this.#approvedName) {
      this.#reset();
    }
    this.#updateFallbackHref();
  }

  /**
   * Filtert Nicht-Buchstaben heraus, hält die Initialen-Felder in Großschrift,
   * springt nach dem ersten Zeichen automatisch ins zweite Feld und verwirft ein
   * bestehendes Monogramm, sobald die Initialen sich ändern.
   * @param {Event} [event]
   */
  handleInitialInput(event) {
    const target = /** @type {HTMLInputElement | undefined} */ (event?.target);
    // Nicht-Buchstaben direkt beim Tippen/Einfügen entfernen, damit die
    // Client-Prüfung deckungsgleich mit der Server-Validierung (\p{L}) ist.
    if (target) target.value = target.value.replace(/\P{L}/gu, '').toUpperCase();
    if (target === this.refs.initial1 && target.value) {
      this.refs.initial2.focus();
      this.refs.initial2.select();
    }
    if (this.#source === 'initials' && this.#rawInitials() !== this.#approvedName) {
      this.#reset();
    }
  }

  /** @param {Event} [event] */
  async generate(event) {
    event?.preventDefault();

    const name = this.refs.nameInput.value.trim();
    if (!this.#requireName()) return;

    this.#setBusy(true, this.refs.generateButton);
    this.#showStatus('Dein Wappen entsteht — einen Moment.', 'info');

    try {
      const res = await fetch(this.dataset.generateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ name, productId: this.dataset.productId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      if (!data?.id || !data?.previewUrl || !data?.vectorUrl) throw new Error('Unvollständige Antwort');

      this.#apply(name, data, 'generated');
      this.#persist();
      this.#showStatus('Wappen bereit. Du kannst es in den Warenkorb legen.', 'info');
    } catch (error) {
      console.error('[signet-engraver]', error);
      this.#reset();
      this.#showStatus('Das Wappen konnte nicht erstellt werden. Bitte versuche es erneut.', 'error');
    } finally {
      this.#setBusy(false);
    }
  }

  /** Erzeugt ein Monogramm aus zwei Initialen. @param {Event} [event] */
  async createInitials(event) {
    event?.preventDefault();

    const initials = this.#rawInitials();
    if (initials.length < 2) {
      (this.refs.initial1.value ? this.refs.initial2 : this.refs.initial1).focus();
      this.#showStatus('Bitte gib beide Initialen ein.', 'error');
      return;
    }

    this.#setBusy(true, this.refs.initialsButton);
    this.#showStatus('Dein Monogramm entsteht — einen Moment.', 'info');

    try {
      const res = await fetch(this.dataset.initialsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ initials, productId: this.dataset.productId }),
      });

      // Antwort auch bei Fehlerstatus lesen — der Server liefert den Fehlercode
      // im JSON-Body (invalid_initials, rate_limited, …).
      const data = await res.json().catch(() => null);
      const errorCode = typeof data?.error === 'string' ? data.error : null;
      if (errorCode || !res.ok || !data?.id || !data?.previewUrl || !data?.vectorUrl) {
        this.#reset();
        this.#showStatus(INITIALS_ERROR_MESSAGES[errorCode] || 'Das Monogramm konnte nicht erstellt werden. Bitte versuche es erneut.', 'error');
        return;
      }

      this.#apply(initials, data, 'initials');
      this.#persist();
      this.#showStatus('Monogramm bereit. Du kannst es in den Warenkorb legen.', 'info');
    } catch (error) {
      console.error('[signet-engraver]', error);
      this.#reset();
      this.#showStatus('Das Monogramm konnte nicht erstellt werden. Bitte versuche es erneut.', 'error');
    } finally {
      this.#setBusy(false);
    }
  }

  /** Öffnet den Dateidialog für ein eigenes Motiv. @param {Event} [event] */
  upload(event) {
    event?.preventDefault();
    // Name ist beim Upload optional — direkt den Dateidialog öffnen.
    this.refs.fileInput.click();
  }

  /** Verarbeitet die gewählte Datei und schickt sie an den Upload-Endpoint. */
  async handleFileSelected() {
    const file = this.refs.fileInput.files?.[0];
    if (!file) return;

    const name = this.refs.nameInput.value.trim();
    if (!UPLOAD_TYPES.includes(file.type)) {
      this.refs.fileInput.value = '';
      this.#showStatus('Dieses Dateiformat wird nicht unterstützt. Bitte verwende PNG, JPG, WebP oder SVG.', 'error');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      this.refs.fileInput.value = '';
      this.#showStatus('Die Datei ist zu groß. Bitte verwende ein Bild bis 10 MB.', 'error');
      return;
    }

    this.#setBusy(true, this.refs.uploadButton);
    this.#showStatus('Dein Motiv wird verarbeitet — einen Moment.', 'info');

    try {
      const image = await this.#fileToPngDataUrl(file);
      const res = await fetch(this.dataset.uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ name, productId: this.dataset.productId, filename: file.name, image }),
      });

      // Antwort auch bei Fehlerstatus lesen — der Server liefert den Fehlercode
      // im JSON-Body (image_no_content, image_low_contrast, …).
      const data = await res.json().catch(() => null);
      const errorCode = typeof data?.error === 'string' ? data.error : null;
      if (errorCode || !res.ok || !data?.id || !data?.previewUrl || !data?.vectorUrl) {
        this.#reset();
        this.#showStatus(UPLOAD_ERROR_MESSAGES[errorCode] || 'Das Motiv konnte nicht übernommen werden. Bitte versuche es erneut.', 'error');
        return;
      }

      this.#apply(name, data, 'uploaded');
      this.#persist();
      this.#showStatus('Dein Motiv ist übernommen. Bitte prüfe die Vorschau und bestätige sie unten, dann kannst du es in den Warenkorb legen.', 'info');
    } catch (error) {
      console.error('[signet-engraver]', error);
      this.#reset();
      this.#showStatus('Das Motiv konnte nicht übernommen werden. Bitte versuche es erneut.', 'error');
    } finally {
      this.#setBusy(false);
      // Zurücksetzen, damit dieselbe Datei erneut gewählt werden kann.
      this.refs.fileInput.value = '';
    }
  }

  /** Übernimmt die Bestätigung des hochgeladenen Motivs in die versteckte Property. */
  handleConfirmToggle() {
    const confirmed = this.refs.confirmCheckbox.checked;
    this.#setConfirmed(confirmed);
    if (confirmed) {
      this.#showStatus('Danke — du kannst das Motiv jetzt in den Warenkorb legen.', 'info');
    }
    this.#persist();
  }

  /**
   * Entfernt das aktuelle Motiv vollständig — Vorschau, versteckte Properties,
   * Eingabefelder und den sessionStorage-Eintrag. Danach ist der Ring ohne
   * Individualisierung bestellbar.
   * @param {Event} [event]
   */
  discard(event) {
    event?.preventDefault();
    this.#reset();
    this.refs.nameInput.value = '';
    this.refs.initial1.value = '';
    this.refs.initial2.value = '';
    this.#updateFallbackHref();
    this.#showStatus('Gravur entfernt. Der Ring ist auch ohne Motiv bestellbar.', 'info');
  }

  /** Fokussiert das Namensfeld und meldet, wenn der Name fehlt. */
  #requireName() {
    if (this.refs.nameInput.value.trim()) return true;
    this.refs.nameInput.focus();
    this.#showStatus('Bitte gib zuerst deinen Familiennamen ein.', 'error');
    return false;
  }

  /** Die beiden Initialen als rohe, großgeschriebene Zwei-Zeichen-Folge. */
  #rawInitials() {
    return (this.refs.initial1.value + this.refs.initial2.value).toUpperCase().slice(0, 2);
  }

  /**
   * Verkleinert die Datei client-seitig auf ≤1024px Kantenlänge und liefert
   * eine PNG-Data-URL — hält den JSON-Body klein (Vercel-Limit 4,5 MB) und
   * normalisiert SVG/WebP gleich mit auf PNG.
   * @param {File} file
   */
  async #fileToPngDataUrl(file) {
    const img = await this.#loadImage(file);
    try {
      const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      const ctx = canvas.getContext('2d');
      // Transparente Bilder auf Weiß legen — entspricht der Server-Pipeline.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img.source, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    } finally {
      img.close();
    }
  }

  /**
   * Lädt die Datei als zeichenbare Quelle. SVG läuft über <img> + Object-URL,
   * weil Firefox createImageBitmap(SVG) nicht unterstützt.
   * @param {File} file
   * @returns {Promise<{ source: CanvasImageSource, width: number, height: number, close: () => void }>}
   */
  async #loadImage(file) {
    if (file.type !== 'image/svg+xml' && typeof createImageBitmap === 'function') {
      try {
        const bmp = await createImageBitmap(file);
        return { source: bmp, width: bmp.width, height: bmp.height, close: () => bmp.close() };
      } catch {
        // Fallback über <img> unten.
      }
    }
    const url = URL.createObjectURL(file);
    try {
      const el = await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Bilddatei konnte nicht gelesen werden'));
        image.src = url;
      });
      // SVGs ohne intrinsische Größe bekommen eine sinnvolle Kantenlänge.
      return {
        source: el,
        width: el.naturalWidth || MAX_EDGE,
        height: el.naturalHeight || MAX_EDGE,
        close: () => URL.revokeObjectURL(url),
      };
    } catch (error) {
      URL.revokeObjectURL(url);
      throw error;
    }
  }

  /**
   * @param {string} name Bindungsschlüssel: Name (Wappen) bzw. rohe Initialen.
   * @param {{id:string,previewUrl:string,vectorUrl:string}} data
   * @param {'generated'|'uploaded'|'initials'} source
   */
  #apply(name, data, source) {
    this.#source = source;
    this.refs.idInput.value = data.id;
    this.refs.previewUrlInput.value = data.previewUrl;
    this.refs.vectorUrlInput.value = data.vectorUrl;
    this.refs.sourceInput.value = SOURCE_LABELS[source] || '';
    // Sichtbare Properties spiegeln immer das aktuelle Motiv: Ein Monogramm trägt
    // die Initialen-Property, ein Wappen bzw. Upload leert sie. Umgekehrt ersetzt
    // ein Monogramm den Familiennamen, damit die Order nie Name und Initialen
    // gemischt trägt (Familienname bleibt bei Wappen/Upload erhalten).
    this.refs.initialsInput.value = source === 'initials' ? formatInitials(name) : '';
    if (source === 'initials') this.refs.nameInput.value = '';
    this.refs.preview.src = data.previewUrl;
    this.refs.preview.alt = this.#previewAlt(source, name);
    this.refs.previewWrap.hidden = false;
    // Bestätigung nur bei hochgeladenen Motiven; frischer Zustand ist unbestätigt.
    this.refs.confirmWrap.hidden = source !== 'uploaded';
    this.#setConfirmed(false);
    this.#approvedName = name;
  }

  /**
   * @param {'generated'|'uploaded'|'initials'} source
   * @param {string} name
   */
  #previewAlt(source, name) {
    if (source === 'initials') return `Monogramm ${formatInitials(name)}`;
    if (source === 'uploaded') return 'Hochgeladenes Motiv';
    return name ? `Wappen für ${name}` : 'Wappen';
  }

  #reset() {
    this.#approvedName = null;
    this.#source = null;
    this.refs.idInput.value = '';
    this.refs.previewUrlInput.value = '';
    this.refs.vectorUrlInput.value = '';
    this.refs.sourceInput.value = '';
    this.refs.initialsInput.value = '';
    this.#setConfirmed(false);
    this.refs.confirmWrap.hidden = true;
    this.refs.previewWrap.hidden = true;
    this.refs.preview.removeAttribute('src');
    this.#clearStorage();
  }

  /**
   * Setzt die aktive Eingabe-Art, blendet den passenden Bereich ein und hält
   * die Radios synchron. @param {'wappen'|'initialen'|'upload'} mode
   * @param {{ persist?: boolean }} [options]
   */
  #setMode(mode, { persist = true } = {}) {
    this.#mode = mode;
    for (const radio of this.refs.modeRadios) radio.checked = radio.value === mode;
    this.refs.wappenArea.hidden = mode !== 'wappen';
    this.refs.initialsArea.hidden = mode !== 'initialen';
    this.refs.uploadArea.hidden = mode !== 'upload';
    if (persist) this.#persist();
  }

  /** Setzt die versteckte Bestätigungs-Property und den Haken. @param {boolean} confirmed */
  #setConfirmed(confirmed) {
    this.refs.confirmCheckbox.checked = confirmed;
    this.refs.confirmedInput.value = confirmed ? 'ja' : '';
  }

  /**
   * Sperrt alle Aktions-Buttons; den Spinner zeigt nur der auslösende Button.
   * @param {boolean} busy @param {HTMLButtonElement} [button]
   */
  #setBusy(busy, button) {
    const buttons = [this.refs.generateButton, this.refs.initialsButton, this.refs.uploadButton].filter(Boolean);
    for (const b of buttons) b.disabled = busy;
    if (busy) {
      (button || buttons[0])?.setAttribute('aria-busy', 'true');
    } else {
      for (const b of buttons) b.removeAttribute('aria-busy');
    }
  }

  /** @param {string} message @param {'info'|'error'} state */
  #showStatus(message, state) {
    const { status } = this.refs;
    status.textContent = message;
    status.dataset.state = state;
    status.hidden = false;
  }

  /**
   * Fängt das Submit des zugehörigen Produktformulars ab, solange ein
   * hochgeladenes Motiv nicht bestätigt ist. Läuft in der Capture-Phase auf
   * window und damit vor dem delegierten Theme-Handler (document, capture).
   * Nur unser eigenes Formular und nur hochgeladene, unbestätigte Motive
   * werden blockiert. @param {Event} event
   */
  #onFormSubmit = (event) => {
    const form = this.refs.sourceInput?.form;
    if (!form || event.target !== form) return;
    if (this.#source !== 'uploaded') return;
    if (this.refs.confirmedInput.value === 'ja') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.refs.confirmWrap.hidden = false;
    this.#showStatus('Bitte bestätige zuerst, dass du dieses Motiv gravieren lassen möchtest.', 'error');
    this.refs.confirmCheckbox.focus();
    this.refs.confirmWrap.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  /**
   * Baut den mailto-Fallback-Link dynamisch auf und spielt den aktuell
   * eingegebenen Familiennamen in die Betreff-Vorlage ein. No-op, wenn kein
   * Fallback-Link gerendert ist (Adresse nicht gesetzt).
   */
  #updateFallbackHref() {
    const link = this.refs.fallbackLink;
    if (!(link instanceof HTMLAnchorElement)) return;
    const email = link.dataset.email;
    if (!email) return;
    const name = this.refs.nameInput.value.trim();
    const subject = link.dataset.subject || '';
    const baseBody = link.dataset.body || '';
    // Nur horizontale Whitespaces ersetzen — die Leerzeilen danach bleiben
    // erhalten. Ersatz als Funktion, damit '$'-Zeichen im Namen ($&, $`, …)
    // nicht als Replacement-Pattern interpretiert werden.
    const body = name ? baseBody.replace(/Familienname:[ \t]*/u, () => `Familienname: ${name}`) : baseBody;
    const params = new URLSearchParams();
    if (subject) params.set('subject', subject);
    if (body) params.set('body', body);
    // Mail-Clients erwarten %20 statt '+' für Leerzeichen.
    const qs = params.toString().replace(/\+/g, '%20');
    link.href = `mailto:${email}${qs ? `?${qs}` : ''}`;
  }

  // --- Persistenz über sessionStorage (überlebt Section-Re-Render und den
  //     Farbwechsel zum Metall-Geschwister-Produkt) -------------------------
  get #storageKey() {
    // Gruppen-Key (sortiertes Gold/Silber-ID-Paar) teilt den Zustand über das
    // Geschwister-Paar; Fallback auf die Produkt-ID für nicht-gruppierte Ringe.
    return `gustho-signet-${this.dataset.signetGroup || this.dataset.productId}`;
  }

  #persist() {
    try {
      const payload = {
        mode: this.#mode,
        name: this.refs.nameInput.value.trim(),
        initials: this.#rawInitials(),
        source: this.#source,
        confirmed: this.refs.confirmedInput.value === 'ja',
        id: this.refs.idInput.value,
        previewUrl: this.refs.previewUrlInput.value,
        vectorUrl: this.refs.vectorUrlInput.value,
      };
      sessionStorage.setItem(this.#storageKey, JSON.stringify(payload));
    } catch {}
  }

  #clearStorage() {
    try {
      sessionStorage.removeItem(this.#storageKey);
    } catch {}
  }

  #hydrateFromStorage() {
    try {
      const raw = sessionStorage.getItem(this.#storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw);

      // Modus wiederherstellen (alte Einträge ohne Feld -> Wappen).
      const mode = MODES.includes(saved?.mode) ? saved.mode : 'wappen';
      this.#setMode(mode, { persist: false });

      // Eingaben zurückspielen.
      if (saved?.name) this.refs.nameInput.value = saved.name;
      if (saved?.initials) {
        this.refs.initial1.value = saved.initials.charAt(0) || '';
        this.refs.initial2.value = saved.initials.charAt(1) || '';
      }

      if (!saved?.id) return; // Kein Motiv gespeichert — nur Modus/Eingaben.

      // Alte Einträge ohne source stammen aus der Generierung.
      const source =
        saved.source === 'uploaded' ? 'uploaded' : saved.source === 'initials' ? 'initials' : 'generated';
      const key = source === 'initials' ? saved.initials || '' : saved.name || '';
      this.#apply(key, saved, source);

      // Bestätigung eines hochgeladenen Motivs übernehmen — es ist dasselbe
      // Motiv, die einmal gegebene Freigabe bleibt gültig.
      if (source === 'uploaded' && saved.confirmed) this.#setConfirmed(true);
    } catch {}
  }
}

customElements.define('gustho-signet-engraver', SignetEngraver);
